import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy,
  Timestamp,
  writeBatch
} from 'firebase/firestore';
import { db } from './firebase.config';
import { JobApplication, ApplicationStatus, ContactMethod, InterviewType } from '../types';

export interface CreateApplicationData {
  companyName: string;
  position: string;
  contactMethod: ContactMethod;
  appliedDate: Date;
  status: ApplicationStatus;
  notes: string;
  companyDetails: {
    name: string;
    address: string;
    city: string;
    postalCode: string;
    country: string;
  };
  attachments: Array<{
    name: string;
    url: string;
    type: string;
  }>;
}

export interface UpdateApplicationData {
  companyName?: string;
  position?: string;
  contactMethod?: ContactMethod;
  appliedDate?: Date;
  status?: ApplicationStatus;
  notes?: string;
  companyDetails?: {
    name: string;
    address: string;
    city: string;
    postalCode: string;
    country: string;
  };
  attachments?: Array<{
    name: string;
    url: string;
    type: string;
  }>;
  interviews?: Array<{
    id: string;
    date: Timestamp;
    type: InterviewType;
    location: string;
    notes: string;
    reminder: boolean;
  }>;
}

class ApplicationService {
  private getApplicationsCollection(userId: string) {
    return collection(db, 'users', userId, 'applications');
  }

  private getApplicationDoc(userId: string, applicationId: string) {
    return doc(db, 'users', userId, 'applications', applicationId);
  }

  // Recupera tutte le candidature di un utente
  async getUserApplications(userId: string): Promise<JobApplication[]> {
    try {
      console.log('📥 Recupero candidature per utente:', userId);
      
      const applicationsRef = this.getApplicationsCollection(userId);
      const q = query(applicationsRef, orderBy('appliedDate', 'desc'));
      const querySnapshot = await getDocs(q);
      
      const applications: JobApplication[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        applications.push({
          id: doc.id,
          ...data,
          appliedDate: data.appliedDate,
          statusHistory: data.statusHistory || [],
          interviews: data.interviews || [],
          attachments: data.attachments || []
        } as JobApplication);
      });
      
      console.log(`✅ Recuperate ${applications.length} candidature`);
      return applications;
    } catch (error) {
      console.error('❌ Errore nel recupero candidature:', error);
      throw new Error('Impossibile recuperare le candidature');
    }
  }

  // Recupera una candidatura specifica
  async getApplication(userId: string, applicationId: string): Promise<JobApplication | null> {
    try {
      console.log('📥 Recupero candidatura:', applicationId);
      
      const applicationRef = this.getApplicationDoc(userId, applicationId);
      const applicationDoc = await getDoc(applicationRef);
      
      if (applicationDoc.exists()) {
        const data = applicationDoc.data();
        return {
          id: applicationDoc.id,
          ...data,
          appliedDate: data.appliedDate,
          statusHistory: data.statusHistory || [],
          interviews: data.interviews || [],
          attachments: data.attachments || []
        } as JobApplication;
      }
      
      return null;
    } catch (error) {
      console.error('❌ Errore nel recupero candidatura:', error);
      throw new Error('Impossibile recuperare la candidatura');
    }
  }

  // Crea una nuova candidatura
  async createApplication(userId: string, data: CreateApplicationData): Promise<JobApplication> {
    try {
      console.log('📝 Creazione nuova candidatura per utente:', userId);
      
      const applicationsRef = this.getApplicationsCollection(userId);
      
      const applicationData = {
        ...data,
        appliedDate: Timestamp.fromDate(data.appliedDate),
        statusHistory: [
          {
            status: data.status,
            timestamp: Timestamp.fromDate(data.appliedDate),
            note: 'Candidatura inserita'
          }
        ],
        interviews: [],
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      };
      
      const docRef = await addDoc(applicationsRef, applicationData);
      
      const newApplication: JobApplication = {
        id: docRef.id,
        userId: userId,
        ...applicationData,
        appliedDate: applicationData.appliedDate,
        statusHistory: applicationData.statusHistory,
        interviews: applicationData.interviews,
        attachments: data.attachments
      };
      
      console.log('✅ Candidatura creata con ID:', docRef.id);
      return newApplication;
    } catch (error) {
      console.error('❌ Errore nella creazione candidatura:', error);
      throw new Error('Impossibile creare la candidatura');
    }
  }

  // Aggiorna una candidatura esistente
  async updateApplication(
    userId: string, 
    applicationId: string, 
    data: UpdateApplicationData
  ): Promise<void> {
    try {
      console.log('📝 Aggiornamento candidatura:', applicationId);
      console.log('👤 User ID:', userId);
      console.log('📊 Dati da aggiornare:', data);
      
      const applicationRef = this.getApplicationDoc(userId, applicationId);
      console.log('🔗 Riferimento documento:', applicationRef.path);
      
      // Verifica che il documento esista
      const docSnapshot = await getDoc(applicationRef);
      if (!docSnapshot.exists()) {
        console.error('❌ Documento non trovato:', applicationRef.path);
        throw new Error(`Candidatura con ID ${applicationId} non trovata`);
      }
      console.log('✅ Documento trovato, procedo con l\'aggiornamento');
      
      const updateData: any = {
        ...data,
        updatedAt: Timestamp.now()
      };
      
      // Converti la data se presente
      if (data.appliedDate) {
        updateData.appliedDate = Timestamp.fromDate(data.appliedDate);
      }
      
      console.log('📝 Dati finali per l\'aggiornamento:', updateData);
      await updateDoc(applicationRef, updateData);
      
      console.log('✅ Candidatura aggiornata con successo');
    } catch (error) {
      console.error('❌ Errore nell\'aggiornamento candidatura:', error);
      throw new Error('Impossibile aggiornare la candidatura');
    }
  }

  // Aggiorna lo stato di una candidatura
  async updateApplicationStatus(
    userId: string,
    applicationId: string,
    newStatus: ApplicationStatus,
    note: string = ''
  ): Promise<void> {
    try {
      console.log('📝 Aggiornamento stato candidatura:', applicationId, '→', newStatus);
      
      const applicationRef = this.getApplicationDoc(userId, applicationId);
      const applicationDoc = await getDoc(applicationRef);
      
      if (!applicationDoc.exists()) {
        throw new Error('Candidatura non trovata');
      }
      
      const currentData = applicationDoc.data();
      const statusHistory = currentData.statusHistory || [];
      
      const newStatusEntry = {
        status: newStatus,
        timestamp: Timestamp.now(),
        note: note || `Stato aggiornato a: ${newStatus}`
      };
      
      await updateDoc(applicationRef, {
        status: newStatus,
        statusHistory: [...statusHistory, newStatusEntry],
        updatedAt: Timestamp.now()
      });
      
      console.log('✅ Stato candidatura aggiornato');
    } catch (error) {
      console.error('❌ Errore nell\'aggiornamento stato:', error);
      throw new Error('Impossibile aggiornare lo stato della candidatura');
    }
  }

  // Elimina una candidatura
  async deleteApplication(userId: string, applicationId: string): Promise<void> {
    try {
      console.log('🗑️ Eliminazione candidatura:', applicationId);
      
      const applicationRef = this.getApplicationDoc(userId, applicationId);
      await deleteDoc(applicationRef);
      
      console.log('✅ Candidatura eliminata');
    } catch (error) {
      console.error('❌ Errore nell\'eliminazione candidatura:', error);
      throw new Error('Impossibile eliminare la candidatura');
    }
  }

  // Aggiunge un colloquio a una candidatura
  async addInterview(
    userId: string,
    applicationId: string,
    interviewData: {
      date: Date;
      type: string;
      location: string;
      notes: string;
      reminder: boolean;
    }
  ): Promise<void> {
    try {
      console.log('📅 Aggiunta colloquio per candidatura:', applicationId);
      
      const applicationRef = this.getApplicationDoc(userId, applicationId);
      const applicationDoc = await getDoc(applicationRef);
      
      if (!applicationDoc.exists()) {
        throw new Error('Candidatura non trovata');
      }
      
      const currentData = applicationDoc.data();
      const interviews = currentData.interviews || [];
      
      const newInterview = {
        id: `interview_${Date.now()}`,
        date: Timestamp.fromDate(interviewData.date),
        type: interviewData.type,
        location: interviewData.location,
        notes: interviewData.notes,
        reminder: interviewData.reminder
      };
      
      await updateDoc(applicationRef, {
        interviews: [...interviews, newInterview],
        updatedAt: Timestamp.now()
      });
      
      console.log('✅ Colloquio aggiunto');
    } catch (error) {
      console.error('❌ Errore nell\'aggiunta colloquio:', error);
      throw new Error('Impossibile aggiungere il colloquio');
    }
  }

  // Sincronizza le candidature locali con Firestore
  async syncApplications(userId: string, localApplications: JobApplication[]): Promise<JobApplication[]> {
    try {
      console.log('🔄 Sincronizzazione candidature per utente:', userId);
      
      const firestoreApplications = await this.getUserApplications(userId);
      
      // Se non ci sono candidature in Firestore, crea quelle locali
      if (firestoreApplications.length === 0 && localApplications.length > 0) {
        console.log('📤 Caricamento candidature locali su Firestore...');
        
        const batch = writeBatch(db);
        
        for (const app of localApplications) {
          const applicationsRef = this.getApplicationsCollection(userId);
          const docRef = doc(applicationsRef);
          
          const applicationData = {
            ...app,
            id: docRef.id,
            appliedDate: app.appliedDate,
            statusHistory: app.statusHistory || [],
            interviews: app.interviews || [],
            attachments: app.attachments || [],
            createdAt: Timestamp.now(),
            updatedAt: Timestamp.now()
          };
          
          batch.set(docRef, applicationData);
        }
        
        await batch.commit();
        console.log('✅ Candidature locali sincronizzate con Firestore');
        
        return localApplications.map(app => ({
          ...app,
          id: `temp_${app.id}` // Mantieni gli ID temporanei per compatibilità
        }));
      }
      
      return firestoreApplications;
    } catch (error) {
      console.error('❌ Errore nella sincronizzazione:', error);
      throw new Error('Impossibile sincronizzare le candidature');
    }
  }
}

export const applicationService = new ApplicationService(); 